import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { fetchChatMessages, fetchUsers, markChatMessagesRead, sendChatMessage } from "../api/api";
import { useUser } from "../UserContext";

const normalizeList = (data) => Array.isArray(data) ? data : (data?.results || []);

const Chat = () => {
    const { user } = useUser();
    const [users, setUsers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [recipient, setRecipient] = useState("");
    const [body, setBody] = useState("");
    const [toAll, setToAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const isAdmin = Boolean(
        user?.is_superuser ||
        user?.is_staff ||
        user?.groups?.includes("Administrator") ||
        user?.groups?.includes("Superuser")
    );

    const availableUsers = useMemo(
        () => users.filter((item) => item.id !== user?.user_id),
        [users, user]
    );

    const loadChat = useCallback(async () => {
        try {
            setError("");
            const [usersData] = await Promise.all([
                fetchUsers(),
                markChatMessagesRead(),
            ]);
            const messagesData = await fetchChatMessages();
            setUsers(normalizeList(usersData));
            setMessages(normalizeList(messagesData));
        } catch (err) {
            console.error("Error loading chat:", err);
            setError("Could not load chat messages.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadChat();
    }, [loadChat]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!body.trim()) {
            setError("Enter a message before sending.");
            return;
        }

        if (!toAll && !recipient) {
            setError("Choose a recipient.");
            return;
        }

        try {
            setSending(true);
            setError("");
            await sendChatMessage({
                recipient: toAll ? null : recipient,
                body: body.trim(),
                to_all: toAll,
            });
            setBody("");
            setToAll(false);
            setRecipient("");
            await loadChat();
        } catch (err) {
            console.error("Error sending chat message:", err);
            setError(err.response?.data?.detail || "Could not send message.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Container fluid className="mt-4 chat-page">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1 className="mb-0">Inhouse Chat</h1>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row className="g-3">
                <Col lg={4}>
                    <Card>
                        <Card.Body>
                            <Card.Title>New Message</Card.Title>
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Send to</Form.Label>
                                    <Form.Select
                                        value={recipient}
                                        disabled={toAll}
                                        onChange={(event) => setRecipient(event.target.value)}
                                    >
                                        <option value="">Select username</option>
                                        {availableUsers.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.username}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                {isAdmin && (
                                    <Form.Check
                                        className="mb-3"
                                        type="checkbox"
                                        label="Send to all users"
                                        checked={toAll}
                                        onChange={(event) => setToAll(event.target.checked)}
                                    />
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={body}
                                        onChange={(event) => setBody(event.target.value)}
                                    />
                                </Form.Group>

                                <Button type="submit" disabled={sending}>
                                    {sending ? "Sending..." : "Send"}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Previous Chats</Card.Title>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" />
                                </div>
                            ) : messages.length === 0 ? (
                                <Alert variant="secondary">No chat messages yet.</Alert>
                            ) : (
                                <div className="chat-thread">
                                    {messages.map((message) => {
                                        const incoming = String(message.recipient) === String(user?.user_id);
                                        return (
                                            <div
                                                key={message.id}
                                                className={`chat-message ${incoming ? "incoming" : "outgoing"}`}
                                            >
                                                <div className="d-flex justify-content-between gap-2">
                                                    <strong>
                                                        {message.sender_name} to {message.sent_to_all ? "All users" : message.recipient_name}
                                                    </strong>
                                                    <span className="text-muted small">
                                                        {new Date(message.created_at).toLocaleString("en-GB")}
                                                    </span>
                                                </div>
                                                <p className="mb-1">{message.body}</p>
                                                {incoming && !message.read_at && <Badge bg="warning" text="dark">Unread</Badge>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Chat;